import { Box, Button, Checkbox, FormControlLabel, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import styles from "../../assets/styles/form.module.css";
import { useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { login } from "../../api/services/auth";
import { handleApiError } from "../../utils/errorHandler";
import { EMAIL_REGEX } from "../../utils/emailValidate";
import { CREATE_ACCOUNT, E_ENTER_EMAIL_ADDRESS, E_ENTER_PASSWORD, LOGIN_SUCCESS_MESSAGE, SIGN_IN, STORAGE_KEY } from "../../utils/constants";
import PasswordAdornment from "../../components/common/PasswordAdornment/PasswordAdornment";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { blue, grey } from "@mui/material/colors";
import PinnacleFormContainer from "../../components/PinnacleFormContainer/PinnacleFormContainer";

export default function LoginPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isRemember, setIsRemember] = useState<boolean>(true);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    const isEmailValid = EMAIL_REGEX.test(email);
    const isPasswordValid = password.length > 0;

    const handleTogglePassword = () => setShowPassword((prev) => !prev);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (!emailTouched) setEmailTouched(true);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (!passwordTouched) setPasswordTouched(true);
        if (loginError) setLoginError(null);
    };

    const handleRememberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsRemember(e.target.checked);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailTouched(true);
        setPasswordTouched(true);

        if (!isEmailValid || !isPasswordValid) {
            return;
        }

        setFormDisabled(true);

        try {
            localStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(STORAGE_KEY);

            const loginResponse = await login({ email, password });
            const token = loginResponse.data?.token;
            const user = loginResponse.data?.user;

            if (!token || !user) {
                throw new Error("Invalid login response.");
            }

            if (isRemember) {
                localStorage.setItem(STORAGE_KEY, token);
            } else {
                sessionStorage.setItem(STORAGE_KEY, token);
            }

            pushNotification({
                details: LOGIN_SUCCESS_MESSAGE,
                type: NotificationTypes.Success,
            });

            if (user.isEula === true) {
                navigate("/");
            } else {
                navigate("/account-setup");
            }
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            setLoginError(errorMessage);
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <PinnacleFormContainer formName={SIGN_IN}>
            <form onSubmit={handleSubmit} noValidate>

                <Box className={styles["form-panel-box"]}>

                    {/* EMAIL */}
                    <PinnacleTextField
                        error={emailTouched && !isEmailValid}
                        color={emailTouched && (!isEmailValid || !!loginError) ? 'error' : 'primary'}
                        helperText={emailTouched && !isEmailValid ? E_ENTER_EMAIL_ADDRESS : ''}
                        id="email"
                        autoFocus
                        label="Email Address"
                        variant="outlined"
                        onChange={handleEmailChange}
                        onBlur={() => setEmailTouched(true)}
                        value={email}
                        autoComplete="email"
                        disabled={formDisabled}
                    />

                    {/* PASSWORD */}
                    <PinnacleTextField
                        error={passwordTouched && (!isPasswordValid || !!loginError)}
                        color={passwordTouched && (!isPasswordValid || !!loginError) ? 'error' : 'primary'}
                        helperText={
                            passwordTouched && !isPasswordValid
                                ? E_ENTER_PASSWORD
                                : loginError
                        }
                        id="password"
                        label="Password"
                        variant="outlined"
                        onChange={handlePasswordChange}
                        onBlur={() => setPasswordTouched(true)}
                        value={password}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        disabled={formDisabled}
                        slotProps={{
                            input: {
                                endAdornment: <PasswordAdornment show={showPassword} toggleShow={handleTogglePassword} />,
                            },
                        }}
                    />
                </Box>

                {/* LOGIN OPTIONS */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isRemember}
                                    onChange={handleRememberChange}
                                    color="primary"
                                    size="small"
                                    sx={{
                                        color: theme.palette.common.white,
                                        '&.Mui-checked': {
                                            color: blue[300],
                                        },
                                    }}
                                />
                            }
                            label="Remember Me"
                            sx={{ color: theme.palette.common.white }}
                        />
                    </Box>
                    <Button
                        variant="text"
                        sx={{ color: theme.palette.common.white, textTransform: "none", fontSize: 14, p: 0, minWidth: "auto" }}
                        onClick={() => navigate("/forgot-password")}
                    >
                        Forgot password?
                    </Button>
                </Box>

                {/* LOGIN BUTTON */}
                <Box sx={{ mt: 2 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={!isEmailValid || !isPasswordValid || formDisabled}
                        sx={{
                            mb: 1.5,
                            '&.Mui-disabled': {
                                backgroundColor: grey[800],
                                color: grey[400],
                                opacity: 1,
                            },
                        }}
                    >
                        {formDisabled ? (
                            <CircularProgress size={24} sx={{ color: blue[300] }} />
                        ) : (
                            SIGN_IN.toUpperCase()
                        )}
                    </Button>
                    <Button
                        fullWidth
                        variant="text"
                        sx={{
                            backgroundColor: 'var(--color-button-neutral)',
                            color: theme.palette.common.white,
                        }}
                        onClick={() => navigate("/register")}
                    >
                        {CREATE_ACCOUNT.toUpperCase()}
                    </Button>
                </Box>
            </form>
        </PinnacleFormContainer>
    );
}